import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Quote, UserCircle2 } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const REL = {
  friend: { en: "Friend", es: "Amigo/a" },
  family: { en: "Family", es: "Familia" },
  former_coworker: { en: "Former coworker", es: "Excompañero/a de trabajo" },
  neighbor: { en: "Neighbor", es: "Vecino/a" },
  other: { en: "Knows them personally", es: "Lo/la conoce personalmente" },
};

function VouchCard({ v, es }) {
  const rel = REL[v.relationship] || REL.other;
  return (
    <figure data-testid={`vouch-card-${v.id}`} className="rounded-3xl border border-sand-300 bg-white p-6">
      <Quote size={20} className="text-clay mb-3" />
      <div className="space-y-3">
        {v.answers.map((a, i) => (
          <div key={i}>
            <p className="text-xs text-charcoal-400">{es ? a.question_es : a.question_en}</p>
            <p className="text-charcoal-700 leading-relaxed">{a.text}</p>
          </div>
        ))}
      </div>
      <figcaption className="mt-4 border-t border-sand-200 pt-3 text-xs text-charcoal-500">
        <span className="font-medium text-charcoal-700">{v.display_name}</span>
        <span className="ml-2 inline-flex items-center gap-1 bg-sage-50 text-sage-800 rounded-full px-2 py-0.5">
          {es ? rel.es : rel.en}{v.years_known ? ` · ${v.years_known} ${es ? "años" : "yrs"}` : ""}
        </span>
        <span className="ml-2 text-[10px] uppercase tracking-wide text-charcoal-400">{v.language}</span>
        {v.created_at && <span className="ml-2 text-charcoal-400">{new Date(v.created_at).toLocaleString(undefined, { month: "short", year: "numeric" })}</span>}
      </figcaption>
    </figure>
  );
}

export default function References() {
  const { lang } = useLang();
  const es = lang === "es";
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/references").then((r) => setData(r.data)).catch(() => setData({ members: [], vouches: [] })); }, []);
  if (!data) return null;

  return (
    <div className="mx-auto max-w-5xl px-6 lg:px-12 py-16" data-testid="references-page">
      <h1 className="text-3xl sm:text-4xl font-normal text-charcoal-800 mb-3">{es ? "Referencias personales" : "Personal references"}</h1>
      <p className="text-charcoal-600 max-w-2xl mb-10">
        {es
          ? "Estas son referencias de personas que conocen a nuestro equipo personalmente. No son reseñas de clientes y no cuentan en nuestra calificación de estrellas."
          : "These are references from people who know our team personally. They are not client reviews and are not counted in our star rating."}
      </p>

      <div className="space-y-12">
        {data.members.map((m) => {
          const vouches = data.vouches.filter((v) => v.team_member_id === m.id);
          return (
            <section key={m.id} data-testid={`ref-member-${m.id}`}>
              <div className="flex items-center gap-4 mb-5">
                {m.photo ? (
                  <img src={m.photo} alt={m.name} className="h-14 w-14 rounded-full object-cover" />
                ) : (
                  <UserCircle2 size={48} className="text-sand-300" />
                )}
                <div className="flex-1">
                  <h2 className="text-xl text-charcoal-800">{m.name}</h2>
                  <p className="text-sm text-charcoal-500">{es ? m.role_es : m.role_en}</p>
                </div>
                {m.accepting_vouches && (
                  <Link to={`/vouch/${m.id}`} data-testid={`leave-vouch-${m.id}`} className="rounded-full bg-terracotta text-white px-5 py-2 text-sm">
                    {es ? `Dejar una referencia para ${m.name}` : `Leave a reference for ${m.name}`}
                  </Link>
                )}
              </div>
              {vouches.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-5">{vouches.map((v) => <VouchCard key={v.id} v={v} es={es} />)}</div>
              ) : (
                <p className="text-sm text-charcoal-400" data-testid={`ref-empty-${m.id}`}>{es ? "Aún no hay referencias publicadas." : "No references published yet."}</p>
              )}
            </section>
          );
        })}
      </div>

      <div className="mt-12 border-t border-sand-200 pt-6 text-sm flex flex-wrap gap-4">
        <Link to="/reviews" className="text-charcoal-600 underline">{es ? "Ver reseñas de clientes" : "See client reviews"}</Link>
        <Link to="/review-policy" className="text-charcoal-500 underline ml-auto">{es ? "Cómo gestionamos las reseñas" : "How we handle reviews"}</Link>
      </div>
    </div>
  );
}
